import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { HttpService } from './http.service';
import { UserDataService } from '../core/user-data.service';
import { LoggingService } from '../core/logging.service';
import { BehaviorSubject } from 'rxjs';
import { AuthSessionService } from './auth-session.service';

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 5000;
const MAX_RETRIES = 3;

@Injectable({
  providedIn: 'root',
})
export class RouteGuardsService {
  private router = inject(Router);
  private http = inject(HttpService);
  private user = inject(UserDataService);
  private logging = inject(LoggingService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private authSession = inject(AuthSessionService);

  public authorized = new BehaviorSubject<boolean>(false);

  async canActivate(route: ActivatedRouteSnapshot) {
    const idPlanta = Number(route.queryParams.idPlanta);
    if (idPlanta) {
      try {
        // TODO CHECKAR PERMISSOES
        await this.user.changePlantaToken(idPlanta);
      } catch {
        this.logging.logInfo('deu ruim trocando de planta');
      }
    }

    const auth_user_data = await this.resolveAuthData();

    if (auth_user_data) {
      if (!auth_user_data.status_planta && auth_user_data.id_cliente === 2) {
        this.router.navigateByUrl('/login');
        this.logging.logInfo('Plant Deactivated 📵');
      }

      await this.user.setData(auth_user_data);

      if (route.data.roles) {
        const can_login = route.data.roles.includes(this.user.getIdRole());
        this.logging.logInfo('🔒role_check', can_login);
        if (can_login) {
          this.authorized.next(true);
        }
        return can_login;
      }

      this.authorized.next(true);
      return true;
    } else {
      this.router.navigateByUrl('/login');
      if (this.hasToken()) {
        this.translate.get('MAP.SESSION_RECONNECT_FAILED').subscribe((message) => {
          this.snackBar.open(message, '', {
            duration: 6000,
            panelClass: 'notification-snackbar',
            verticalPosition: 'top',
          });
        });
        this.logging.logError(
          'LOGOUT_GUARD_INVALID_TOKEN',
          'pweb_auth com token inválido na revalidação'
        );
      } else {
        this.logging.logError('LOGOUT_GUARD_NO_TOKEN', 'pweb_auth sem token após revalidação');
      }
      return false;
    }
  }

  private async resolveAuthData(): Promise<any> {
    let attempt = 0;
    let reconnectingShown = false;

    try {
      while (this.hasToken() && attempt < MAX_RETRIES) {
        const data = await this.http.maestroUserGet('v2/pweb_auth', true);

        if (data) {
          return data;
        }

        if (!this.hasToken()) {
          break;
        }

        attempt++;
        this.logging.logError(
          'pweb_auth',
          `Falha transitória ao revalidar sessão (tentativa ${attempt}); usuário mantido`
        );

        if (!reconnectingShown) {
          this.snackBar.open(this.translate.instant('MAP.RECONNECTING'), '', {
            panelClass: 'notification-snackbar',
            verticalPosition: 'top',
          });
          reconnectingShown = true;
        }

        await this.delay(
          Math.min(RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt - 1), RECONNECT_MAX_DELAY_MS)
        );
      }

      return null;
    } finally {
      if (reconnectingShown) {
        this.snackBar.dismiss();
      }
    }
  }

  private hasToken(): boolean {
    return this.authSession.hasToken();
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
